import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { captureError } from '@/lib/sentry';
import { trackAccessEvent } from '@/lib/access-telemetry';
import { useExamSession } from '@/contexts/ExamSessionContext';
import { isExamOrPracticeActive } from '@/lib/safe-storage';
import {
  SESSION_ID_KEY as REGISTRY_SESSION_ID_KEY,
  getSessionId,
  isSessionRegistered,
  clearSessionRegistry,
  logSessionDecision,
} from '@/lib/session-registry';

export const useSessionManager = () => {
  const { user, session, signOut } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { examInProgress } = useExamSession();
  const validationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastLoginTimeRef = useRef<number>(0);
  const isPageVisibleRef = useRef<boolean>(true);
  const lastValidationAtRef = useRef<number>(0);
  const pendingInvalidationRef = useRef<{ message?: string | null } | null>(null);
  const examInProgressRef = useRef<boolean>(false);
  const validateSessionRef = useRef<() => Promise<void>>(async () => {});
  const invalidStreakRef = useRef<number>(0);
  const isAndroid = /android/i.test(navigator.userAgent);

  const SESSION_ID_KEY = REGISTRY_SESSION_ID_KEY;
  // 20s: on a cold iOS launch over cellular the `register` edge function can
  // take several seconds. Validating before the row exists produced a false
  // "session invalidated" logout right after login (P0 iOS bug).
  const INITIAL_DELAY = 20000;
  const VALIDATION_INTERVAL = isAndroid ? 20000 : 30000; // 20s Android / 30s otros
  const VISIBILITY_REVALIDATE_THROTTLE = 10000; // 10s

  // Read-only. We must NEVER mint a new session_id here: this hook only
  // validates, it does not register. Minting a fresh UUID and validating it
  // guarantees `valid: false` (it was never inserted in active_sessions) and
  // forces a wrongful logout — exactly the post-deploy bug we hit when a
  // cache cleanup wiped the stored id. Registration happens only in
  // `signIn()` inside useAuth.
  const getCurrentSessionId = (): string | null => {
    return getSessionId();
  };

  // Number of consecutive server-confirmed `valid:false` answers required
  // before we actually sign the user out. One answer is not evidence: it can
  // race with registration or a transient edge/DB hiccup.
  const INVALID_CONFIRMATIONS_REQUIRED = 2;

  // Track when user logs in
  useEffect(() => {
    if (session) {
      lastLoginTimeRef.current = Date.now();
      console.log(`📱 [SESSION-${isAndroid ? 'ANDROID' : 'OTHER'}] User logged in, tracking time`);
    }
  }, [session, isAndroid]);

  // Keep a ref of examInProgress so visibilitychange listener can read it
  useEffect(() => {
    examInProgressRef.current = examInProgress;
  }, [examInProgress]);

  // Bypass session validation in Lovable preview environment ONLY.
  const isLovablePreview =
    typeof window !== 'undefined' &&
    (window.location.hostname.endsWith('.lovableproject.com') ||
      window.location.hostname.includes('id-preview--'));

  // Deferred logout: when exam ends and we have a pending invalidation, execute it
  useEffect(() => {
    if (examInProgress) return;
    const pending = pendingInvalidationRef.current;
    if (!pending) return;
    if (!user || !session) {
      pendingInvalidationRef.current = null;
      return;
    }
    console.log('🛡️ [SESSION] Exam finished — applying deferred invalidation logout');
    pendingInvalidationRef.current = null;
    (async () => {
      trackAccessEvent('session_lost_unexpected', 'session_manager', {
        reason: 'invalidated_remote_deferred_after_exam',
        android: isAndroid,
        message: pending.message ?? null,
      });
      toast({
        title: 'Sesión cerrada',
        description: 'Tu cuenta fue abierta en otro dispositivo durante el examen. Tu intento se guardó y esta sesión se cerró.',
        variant: 'destructive',
      });
      clearSessionRegistry();
      if (isAndroid) {
        localStorage.clear();
        sessionStorage.clear();
      }
      await signOut();
      if (isAndroid) {
        window.location.href = '/auth/login';
      } else {
        navigate('/auth/login');
      }
    })();
  }, [examInProgress, user, session, isAndroid, signOut, toast, navigate]);

  // Validate session periodically
  useEffect(() => {
    if (isLovablePreview) {
      console.log('🧪 [SESSION] Lovable preview detected — session validation bypassed (preview only).');
      return;
    }
    if (!user || !session) {
      if (validationIntervalRef.current) {
        clearInterval(validationIntervalRef.current);
        validationIntervalRef.current = null;
      }
      return;
    }

    const validateSession = async () => {
      try {
        if (!isPageVisibleRef.current) {
          console.log(`⏸️ [SESSION-${isAndroid ? 'ANDROID' : 'OTHER'}] Skipping - page not visible`);
          return;
        }
        if (!user || !session) return;

        // CRITICAL: never run validation during an active exam/practice.
        if (examInProgressRef.current || isExamOrPracticeActive()) {
          console.log(`⏸️ [SESSION-${isAndroid ? 'ANDROID' : 'OTHER'}] Skipping - exam in progress`);
          return;
        }

        const timeSinceLogin = Date.now() - lastLoginTimeRef.current;
        if (timeSinceLogin < INITIAL_DELAY) {
          console.log(`⏸️ [SESSION-${isAndroid ? 'ANDROID' : 'OTHER'}] Skipping - too soon (${Math.round(timeSinceLogin/1000)}s)`);
          return;
        }

        lastValidationAtRef.current = Date.now();
        const sessionId = getCurrentSessionId();
        if (!sessionId) {
          // No registered session id on this device (e.g. survived a cache
          // wipe). Do NOT validate — that would always fail and log the
          // user out. Leave them signed in; the next explicit signIn will
          // register a fresh id.
          console.warn(`⚠️ [SESSION-${isAndroid ? 'ANDROID' : 'OTHER'}] No session_id in storage — skipping validation to avoid false logout`);
          return;
        }
        // Only validate ids the server CONFIRMED it inserted. An unconfirmed
        // id (register still in flight, or it failed) would always answer
        // `valid:false` and force a wrongful logout.
        if (!isSessionRegistered(sessionId)) {
          logSessionDecision('validation_skipped_unregistered', { android: isAndroid });
          return;
        }
        console.log(`🔍 [SESSION-${isAndroid ? 'ANDROID' : 'OTHER'}] Validating session:`, sessionId);

        const response = await supabase.functions.invoke('manage-session', {
          body: { action: 'validate', session_id: sessionId },
        });

        if (response.error) {
          // Network/edge error is NEVER evidence of a remote login.
          invalidStreakRef.current = 0;
          logSessionDecision('validation_network_error', { android: isAndroid });
          console.error(`❌ [SESSION-${isAndroid ? 'ANDROID' : 'OTHER'}] Validation error:`, response.error);
          return;
        }

        const data = response.data as { valid: boolean; message?: string };
        console.log(`✅ [SESSION-${isAndroid ? 'ANDROID' : 'OTHER'}] Validation result:`, data.valid);

        if (data.valid) {
          invalidStreakRef.current = 0;
        }

        if (!data.valid) {
          invalidStreakRef.current += 1;
          if (invalidStreakRef.current < INVALID_CONFIRMATIONS_REQUIRED) {
            // Require a second, independent confirmation before destroying the
            // session. Kills the register/validate race and one-off blips.
            logSessionDecision('invalidation_unconfirmed', {
              android: isAndroid,
              streak: invalidStreakRef.current,
            });
            return;
          }
          logSessionDecision('logout_session_invalidated_confirmed', {
            android: isAndroid,
            streak: invalidStreakRef.current,
            source: 'useSessionManager.validateSession',
          });
          // If an exam started between the request and the response, defer the logout.
          if (examInProgressRef.current || isExamOrPracticeActive()) {
            console.log('🛡️ [SESSION] Invalidation received during exam — deferring logout until finalize');
            pendingInvalidationRef.current = { message: data.message ?? null };
            return;
          }

          console.log(`🚫 [SESSION-${isAndroid ? 'ANDROID' : 'OTHER'}] Session invalidated - signing out`);
          trackAccessEvent('session_lost_unexpected', 'session_manager', {
            reason: 'invalidated_remote',
            android: isAndroid,
            message: data.message ?? null,
          });
          toast({
            title: 'Sesión cerrada',
            description: 'Tu cuenta ha sido abierta en otro dispositivo. Por seguridad, esta sesión se ha cerrado.',
            variant: 'destructive',
          });

          clearSessionRegistry();
          if (isAndroid) {
            localStorage.clear();
            sessionStorage.clear();
          }
          await signOut();
          if (isAndroid) {
            window.location.href = '/auth/login';
          } else {
            navigate('/auth/login');
          }
        }
      } catch (error) {
        console.error(`❌ [SESSION-${isAndroid ? 'ANDROID' : 'OTHER'}] Exception:`, error);
        captureError(error, { flow: 'session_validation', isAndroid });
      }
    };

    validateSessionRef.current = validateSession;

    console.log(`⏰ [SESSION-${isAndroid ? 'ANDROID' : 'OTHER'}] Setting up validation - delay: ${INITIAL_DELAY}ms, interval: ${VALIDATION_INTERVAL}ms`);

    const initialTimeout = setTimeout(() => {
      validateSession();
      validationIntervalRef.current = setInterval(validateSession, VALIDATION_INTERVAL);
      console.log(`🔄 [SESSION-${isAndroid ? 'ANDROID' : 'OTHER'}] Periodic validation started`);
    }, INITIAL_DELAY);

    return () => {
      console.log(`🛑 [SESSION-${isAndroid ? 'ANDROID' : 'OTHER'}] Cleaning up validation timers`);
      clearTimeout(initialTimeout);
      if (validationIntervalRef.current) {
        clearInterval(validationIntervalRef.current);
      }
    };
  }, [user, session, signOut, toast, navigate, isAndroid, isLovablePreview, INITIAL_DELAY, VALIDATION_INTERVAL]);

  // Visibility change: pause when hidden, revalidate on return (throttled).
  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = !document.hidden;
      isPageVisibleRef.current = visible;
      console.log('👁️ [SESSION] Page visibility changed:', visible ? 'visible' : 'hidden');

      if (!visible) return;
      if (!user || !session) return;
      if (examInProgressRef.current || isExamOrPracticeActive()) return;
      if (isLovablePreview) return;

      const sinceLast = Date.now() - lastValidationAtRef.current;
      if (sinceLast < VISIBILITY_REVALIDATE_THROTTLE) return;

      const sinceLogin = Date.now() - lastLoginTimeRef.current;
      if (sinceLogin < INITIAL_DELAY) return;

      console.log('🔁 [SESSION] Foreground returned — revalidating');
      validateSessionRef.current?.();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, session, isLovablePreview, INITIAL_DELAY]);

  return null;
};
